/**
 * アカウント設定 — 送金の受取先を登録する。
 *
 * ここで登録した内容は、グループで「自分」を紐づけたときに
 * 自分への送金の送金先として表示される。
 */
import {
  useDeletePayoutAccount,
  useFeatures,
  usePayoutAccount,
  useSavePayoutAccount,
  useSpli,
} from "@spli/app-logic";
import {
  PAYOUT_ACCOUNT_KIND_LABELS,
  payoutAccountKindSchema,
  type PayoutAccountKind,
} from "@spli/core";
import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { EmptyState, ErrorView, LoadingView } from "../components/StatusViews.js";
import { Icon } from "../components/Icon.js";

export function AccountPage() {
  const { user, initializing } = useSpli();
  const features = useFeatures();
  const accountQuery = usePayoutAccount({ enabled: features.payoutAccounts });
  const save = useSavePayoutAccount();
  const remove = useDeletePayoutAccount();

  const [kind, setKind] = useState<PayoutAccountKind>("bank");
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [accountType, setAccountType] = useState<"ordinary" | "checking">("ordinary");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [handle, setHandle] = useState("");
  const [note, setNote] = useState("");

  // 保存済みの内容をフォームへ反映する
  const account = accountQuery.data;
  useEffect(() => {
    if (!account) return;
    setKind(account.kind);
    setBankName(account.bankName ?? "");
    setBranchName(account.branchName ?? "");
    setAccountType(account.accountType ?? "ordinary");
    setAccountNumber(account.accountNumber ?? "");
    setAccountHolder(account.accountHolder ?? "");
    setHandle(account.handle ?? "");
    setNote(account.note ?? "");
  }, [account]);

  if (initializing) return <LoadingView />;
  if (!user) return <Navigate to="/login" replace />;

  // 受取先の機能が無効なら、フォーム自体を出さない
  if (!features.payoutAccounts) {
    return (
      <section className="card">
        <h1 className="card__title">アカウント設定</h1>
        <EmptyState icon="bank">
          送金の受取先を登録する機能は、現在ご利用いただけません。
        </EmptyState>
      </section>
    );
  }

  if (accountQuery.isPending) return <LoadingView />;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    save.mutate({
      kind,
      bankName: kind === "bank" ? bankName : null,
      branchName: kind === "bank" ? branchName : null,
      accountType: kind === "bank" ? accountType : null,
      accountNumber: kind === "bank" ? accountNumber : null,
      accountHolder: kind === "bank" ? accountHolder : null,
      handle: kind === "bank" ? null : handle,
      note: note.trim() === "" ? null : note,
    });
  };

  return (
    <section className="card">
      <h1 className="card__title">送金の受取先</h1>
      <p className="card__hint">
        ここに登録しておくと、グループで「自分」を選んで紐づけたときに、
        自分への送金の送金先として表示されます。毎回チャットで口座を送る必要がなくなります。
      </p>
      <p className="notice" style={{ marginBottom: 16 }}>
        <Icon name="warning" size={18} /> 受取先は、紐づけたグループの共有 URL を知っている人に表示されます。
        知らない人に見せたくない場合は登録しないでください (いつでも削除できます)。
      </p>

      <form onSubmit={handleSubmit}>
        <div className="field">
          <span className="field__label">受取方法</span>
          <div className="chip-row">
            {payoutAccountKindSchema.options.map((option) => (
              <button
                key={option}
                type="button"
                className={`chip${kind === option ? " chip--selected" : ""}`}
                aria-pressed={kind === option}
                onClick={() => setKind(option)}
              >
                {PAYOUT_ACCOUNT_KIND_LABELS[option]}
              </button>
            ))}
          </div>
        </div>

        {kind === "bank" ? (
          <>
            <div className="field">
              <label className="field__label" htmlFor="payout-bank">
                銀行名
              </label>
              <input
                id="payout-bank"
                className="input"
                value={bankName}
                placeholder="〇〇銀行"
                onChange={(event) => setBankName(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="payout-branch">
                支店名
              </label>
              <input
                id="payout-branch"
                className="input"
                value={branchName}
                onChange={(event) => setBranchName(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="payout-type">
                預金種別
              </label>
              <select
                id="payout-type"
                className="select"
                value={accountType}
                onChange={(event) =>
                  setAccountType(event.target.value as "ordinary" | "checking")
                }
              >
                <option value="ordinary">普通</option>
                <option value="checking">当座</option>
              </select>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="payout-number">
                口座番号
              </label>
              <input
                id="payout-number"
                className="input"
                inputMode="numeric"
                value={accountNumber}
                onChange={(event) => setAccountNumber(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="payout-holder">
                口座名義
              </label>
              <input
                id="payout-holder"
                className="input"
                value={accountHolder}
                placeholder="ヤマダ タロウ"
                onChange={(event) => setAccountHolder(event.target.value)}
              />
            </div>
          </>
        ) : (
          <div className="field">
            <label className="field__label" htmlFor="payout-handle">
              {kind === "paypay" ? "PayPay ID" : "受取先"}
            </label>
            <input
              id="payout-handle"
              className="input"
              value={handle}
              onChange={(event) => setHandle(event.target.value)}
            />
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="payout-note">
            補足 (任意)
          </label>
          <input
            id="payout-note"
            className="input"
            value={note}
            placeholder="例: 少額なら PayPay でも大丈夫です"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        {save.isError && <ErrorView error={save.error} />}

        <button type="submit" className="button button--block" disabled={save.isPending}>
          {save.isSuccess ? "保存しました" : "保存する"}
        </button>
      </form>

      {account && (
        <div className="row row--end" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="button button--danger button--small"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm("登録した受取先を削除しますか?")) remove.mutate();
            }}
          >
            受取先を削除
          </button>
        </div>
      )}
    </section>
  );
}
